// ## ClientAuthNFsm
//
// State machine for AuthN status of Client (Flash/HTML5) Auth.

'use strict';

var App = require('./app');
var _ = require('./vendor/underscore');
var machina = require('./vendor/machina');
var _logger = require('./logger');
var MVPDPicker = require('./mvpdpicker');
var Store = require('./store');


var ClientAuthNFsm = machina.Fsm.extend({

	supports : {
		autoAuth : true
	},

	initialize : function () {

		_.bindAll(this, 'onPickerOpened', 'onPickerClosed', 'requestAutoAuth');

		MVPDPicker.on('opened', this.onPickerOpened);
		MVPDPicker.on('closed', this.onPickerClosed);

		this.on('handling', function (data) {
			_logger.debug('[ClientAuthNFsm:' + this.state + '] handling ' + data.inputType, arguments);
		});
		this.on('handled', function (data) {
			_logger.debug('[ClientAuthNFsm:' + this.state + '] handled ' + data.inputType, arguments);
		});
		this.on('nohandler', function (data) {
			_logger.debug('[ClientAuthNFsm:' + this.state + '] nohandler', data.inputType, arguments);
		});
		this.on('transition', function (data) {
			_logger.debug('[ClientAuthNFsm:' + this.state + '] transition from "' + data.fromState + '" to "' + data.toState + '" via "' + data.action + '"', arguments);
		});
		this.on('invalidstate', function () {
			_logger.debug('[ClientAuthNFsm:' + this.state + '] invalidstate', arguments);
		});
		this.on('deferred', function () {
			_logger.debug('[ClientAuthNFsm:' + this.state + '] deferred', arguments);
		});

	},

	onPickerOpened : function (obj) {
		_logger.debug('PICKER OPENED!');
		this.handle('pickerOpened', obj);
	},
	onPickerClosed : function (obj) {
		_logger.debug('PICKER CLOSED!');
		this.handle('pickerClosed', obj);
	},

	// Enabling AutoAuth and setting retry limit/delay
	allowsAutoAuth : false,
	autoAuthAttempts : 0,
	autoAuthRetryDelay : 0,
	autoAuthRetryDelayMax : 10000,
	autoAuthRetryLimit : 0,
	autoAuthRetryLimitMax : 8,
	autoAuthStoreKey : 'cvp.auth.autoAuthAttempt',
	autoAuthStoreExpiry : 60000,
	autoauthRetryTimer : null,

	setAutoAuth : function (bool) {
		this.allowsAutoAuth = bool;
	},

	setAutoAuthMvpdId : function (mvpdId) {
		this.autoAuthMvpdId = mvpdId;
	},

	setAutoAuthRetryDelay : function (num) {
		this.autoAuthRetryDelay = Math.min(Math.max(0, Number(num) || 0), this.autoAuthRetryDelayMax);
	},

	setAutoAuthRetryLimit : function (num) {
		this.autoAuthRetryLimit = Math.min(Math.max(0, Number(num) || 0), this.autoAuthRetryLimitMax);
	},

	recordAutoAuthAttempt : function () {
		var localCount = this.autoAuthAttempts;
		var sessionCount = (
			Store.supportsSessionStorage()
			? (Number(Store.getSession(this.autoAuthStoreKey)) || 0)
			: 0
		);

		this.autoAuthAttempts = Math.max(localCount, sessionCount) + 1;

		if (Store.supportsSessionStorage()) {
			Store.setSession(this.autoAuthStoreKey, this.autoAuthAttempts, { expires: _.now() + this.autoAuthStoreExpiry });
		}
	},

	resetAutoAuthAttempt : function () {
		this.autoAuthAttempts = 0;
		if (Store.supportsSessionStorage()) {
			Store.removeSession(this.autoAuthStoreKey);
		}
	},

	canAttemptAutoAuth : function () {
		if (!this.allowsAutoAuth) {
			_logger.debug('auto-auth is not enabled');
			return false;
		}

		if (this.autoAuthAttempts > this.autoAuthRetryLimit) {
			_logger.debug('auto-auth has attempted ' + this.autoAuthAttempts + ' times, meeting/exceeding the retry limit of ' + this.autoAuthRetryLimit);
			return false;
		}

		return true;
	},

	requestCheckPreauthZ : function (resourceIds) {
		_logger.log('requestCheckPreauthZ', resourceIds);
		this.authManager._checkPreauthZ(resourceIds);
	},

	requestCheckAuthN : function () {
		_logger.log('requestCheckAuthN');
		this.authManager._checkAuthN();
	},

	requestGetAuthN : function (redirectUrl) {
		_logger.log('requestGetAuthN', redirectUrl);
		this.clearError();
		this.authManager._getAuthN(redirectUrl);
	},

	requestLogOut : function () {
		this.authManager._logout();
	},

	requestAutoAuth : function () {
		_logger.log('requestAutoAuth');

		this.recordAutoAuthAttempt();
		MVPDPicker.deactivate();
		this.requestGetAuthN();
		this.authManager.selectProvider(this.autoAuthMvpdId);
	},

	clearAttempt : function () {
		if (this.autoauthRetryTimer) {
			window.clearTimeout(this.autoauthRetryTimer);
			this.autoauthRetryTimer = null;
		}
	},

	retryAutoAuth : function () {
		_logger.log('retryAutoAuth with ' + this.autoAuthRetryDelay + ' ms delay');

		this.clearAttempt();

		this.autoauthRetryTimer = window.setTimeout(this.requestAutoAuth, this.autoAuthRetryDelay);
	},

	cancelAutoAuth : function () {
		_logger.log('cancelAutoAuth');

		this.clearAttempt();

		_logger.debug('reactivating Picker');
		MVPDPicker.reactivate();
	},

	initErrors : [],

	getInitErrors : function () {
		return this.initErrors.slice(0);
	},

	constraints : {
		'Initialized' : {
			nextState : 'Unauthenticated',
			checkList : {
				hasErrorEvent : false,
				hasFlowEvent : false
			}
		},
		'Authenticating' : {
			nextState : 'Unauthenticated',
			checkList : {
				hasErrorEvent : false,
				hasFlowEvent : false
			}
		},
		'UserLogIn' : {
			nextState : 'Unauthenticated',
			checkList : {
				hasErrorEvent : false,
				hasFlowEvent : false
			}
		},
		'UserLogOut' : {
			nextState : 'Unauthenticated',
			checkList : {
				hasErrorEvent : false,
				hasFlowEvent : false
			}
		},
		'AutoAuthing' : {
			nextState : 'Unauthenticated',
			checkList : {
				hasErrorEvent : false,
				hasFlowEvent : false
			}
		}
	},

	isMoreSevereThanCurrentError : function (err) {
		var errorCategory = 'errorCategory';
		var severityList = App.ErrorCategory.severity;

		if (errorCategory in err && errorCategory in this._error && _.isArray(severityList)) {
			return _.indexOf(severityList, err.errorCategory) > _.indexOf(severityList, this._error.errorCategory);
		}

		return false;
	},

	setError : function (err) {
		if (!this._error || this.isMoreSevereThanCurrentError(err)) {
			this._error = err;
		}
	},

	clearError : function () {
		delete this._error;
	},

	resetConstraints : function () {
		var state = this.state;
		switch (state) {
			case 'Initialized':
			case 'Authenticating':
			case 'UserLogIn':
			case 'UserLogOut':
			case 'AutoAuthing':
				_logger.debug('[ClientAuthNFsm:' + state + '] resetting constraints for state "' + state + '"');
				_.each(this.constraints[state].checkList, function (constraint, i, list) {
					list[i] = false;
				});
			break;
			default:
				_logger.debug('[ClientAuthNFsm:' + this.state + '] no constraints to reset for state "' + state + '"');
		}
	},

	proceedIfAllConstraintsAreMet : function () {
		var state = this.state;
		var constraints = this.constraints[state];
		if (!constraints) {
			_logger.debug('[ClientAuthNFsm:' + state + '] no constraints for state "' + state + '"');
			return;
		}
		if (_.all(constraints.checkList, _.identity)) {
			this.transition(constraints.nextState);
		}
	},

	initialState : 'Uninitialized',

	states : {

		'Uninitialized' : {
			'init' : 'Initializing'
		},

		'Initializing' : {

			'ready' : function (obj) {
				this.transition('Initialized');
				this.emit('ready', obj);
			},

			'error' : function (obj) {
				if (obj.errorCategory && obj.errorCategory === App.ErrorCategory.BLOCKED) {
					_logger.error('[ClientAuthNFsm:' + this.state + '] Error ' + obj.errorCode + ': Fatal error during auth init!');
					this.emit('initFailure', obj);
					this.transition('InitFailed');
				}
				else {
					_logger.debug('[ClientAuthNFsm:' + this.state + '] Error ' + obj.errorCode + ': Non-fatal error during auth init.');
					this.initErrors.push(obj);
				}
			},

			'failure' : function (obj) {
				this.emit('initFailure', obj);
				this.transition('InitFailed');
			}

		},

		'InitFailed' : {
			_onEnter : function () {
				this.authManager.off('error', this.handleAuthError, this);
			}
		},

		'Initialized' : {

			_onEnter : function () {
				this.resetConstraints();
			},

			'authNPass' : function (obj) {
				this._success = obj;
				this.transition('Authenticated');
			},

			'authNFail' : function () {
				this.constraints[this.state].checkList.hasFlowEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			'error' : function (obj) {
				this.setError(obj);
				this.constraints[this.state].checkList.hasErrorEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			'checkAuthN' : function () {
				this.transition('Authenticating');
				this.requestCheckAuthN();
			},

			'getAuthN' : function (redirectUrl) {
				this.transition('Authenticating');
				this.requestGetAuthN(redirectUrl);
			}

		},

		'Unauthenticated' : {

			_onEnter : function () {
				if (!this._error) {
					_logger.warn('Unauthenticated but we do not know how/why');
					return;
				}
				this.emit('authNFail', this._error);
				this.clearError();

				if (!MVPDPicker.isOpen() && this.canAttemptAutoAuth() && !this.isSwitchingProviders) {
					_logger.debug('[ClientAuthNFsm:' + this.state + '] Can auto-auth!');

					this.transition('AutoAuthing');
				}
			},

			'authNPass' : function (obj) {
				this._success = obj;
				this.transition('Authenticated');
			},

			'checkAuthN' : function () {
				this.transition('Authenticating');
				this.requestCheckAuthN();
			},

			'getAuthN' : function (redirectUrl) {
				this.isSwitchingProviders = false;
				this.transition('Authenticating');
				this.requestGetAuthN(redirectUrl);
			}

		},

		'Authenticating' : {

			_onEnter : function () {
				this.resetConstraints();
			},

			'pickerOpened' : 'UserLogIn',

			'authNPass' : function (obj) {
				this._success = obj;
				this.transition('Authenticated');
			},

			'authNFail' : function () {
				this.constraints[this.state].checkList.hasFlowEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			'error' : function (obj) {
				this.setError(obj);
				this.constraints[this.state].checkList.hasErrorEvent = true;
				this.proceedIfAllConstraintsAreMet();
			}

		},

		// ignore all calls until picker is closed
		'UserLogIn' : {

			_onEnter : function () {
				this.resetConstraints();
			},

			'pickerClosed' : 'Authenticating',

			'authNPass' : function (obj) {
				this._success = obj;
				this.transition('Authenticated');
			},

			'authNFail' : function () {
				this.constraints[this.state].checkList.hasFlowEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			'error' : function (obj) {
				this.setError(obj);
				this.constraints[this.state].checkList.hasErrorEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			'getAuthN' : function (/*redirectUrl*/) {
				this.deferUntilTransition('Unauthenticated');
				this.authManager.selectProvider(null);
			}

		},

		// ignore all calls until logged out
		'UserLogOut' : {

			_onEnter : function () {
				this.resetConstraints();
				this.requestLogOut();
			},

			'authNPass' : function (obj) {
				this._success = obj;
				this.transition('Authenticated');
			},

			'authNFail' : function () {
				this.constraints[this.state].checkList.hasFlowEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			'error' : function (obj) {
				this.setError(obj);
				this.constraints[this.state].checkList.hasErrorEvent = true;
				this.proceedIfAllConstraintsAreMet();
			}

		},

		'Authenticated' : {

			_onEnter : function () {
				if (!this._success) {
					_logger.warn('Authenticated but we do not know how/why');
					return;
				}
				this.emit('authNPass', this._success);
				delete this._success;

				// if authlib works, you should have clean slate to retry all over again.
				this.resetAutoAuthAttempt();
			},

			'error' : function (obj) {
				this.setError(obj);
				this.transition('Unauthenticated');
			},

			'checkPreauthZ' : function (resourceIds) {
				this.requestCheckPreauthZ(resourceIds);
			},

			'checkAuthN' : function () {
				this.transition('Authenticating');
				this.requestCheckAuthN();
			},

			'getAuthN' : function (/*redirectUrl*/) {
				// if you insist...  (only expecting this to be used while authN'ed with TempPass)
				// (we need to setSelectedProvider null before getAuthN will work)
				this.deferUntilTransition('Unauthenticated');
				this.isSwitchingProviders = true;
				this.authManager.selectProvider(null);
			},

			'logout' : 'UserLogOut'

		},

		'AutoAuthing' : {

			_onEnter : function () {
				this.resetConstraints();
				this.requestAutoAuth();
			},

			'authNPass' : function (obj) {
				if (obj.mvpdId === this.autoAuthMvpdId) {
					_logger.debug('[AutoAuth:' + this.state + '] Logged in with "' + this.autoAuthMvpdId + '"!');
				}
				else {
					_logger.debug('[AutoAuth:' + this.state + '] Logged in with a provider.');
				}
				this._success = obj;
				this.transition('Authenticated');
			},

			'authNFail' : function () {
				if (this.canAttemptAutoAuth()) {
					_logger.debug('[AutoAuth:' + this.state + '] Retrying auto-auth with "' + this.autoAuthMvpdId + '".');
					this.resetConstraints();
					this.retryAutoAuth();
				}
				else {
					_logger.warn('[AutoAuth:' + this.state + '] Unable to auto-auth with "' + this.autoAuthMvpdId + '" because of too many attempts.');
					this.constraints[this.state].checkList.hasFlowEvent = true;
					this.proceedIfAllConstraintsAreMet();
				}
			},

			'error' : function (obj) {
				if (this._error) {
					_logger.warn('[AutoAuth:' + this.state + '] Ignoring error "' + obj.errorCode + '" because we already received an error "' + this._error.errorCode + '".');
					return;
				}
				this.setError(obj);
				this.constraints[this.state].checkList.hasErrorEvent = true;
				this.proceedIfAllConstraintsAreMet();
			},

			_onExit : function () {
				this.cancelAutoAuth();
			}

		}

	},

	init : function (AuthManager) {
		if (!AuthManager) {
			throw new Error('AuthManager required for init!');
		}

		this.authManager = AuthManager;

		this.authManager.on('error', this.handleAuthError, this);
		this.handle('init');
	},

	handleInitReady : function (obj) {
		this.handle('ready', obj);
	},

	handleInitFailure : function (obj) {
		this.handle('failure', obj);
	},

	handleAuthNPass : function (obj) {
		this.handle('authNPass', obj);
	},

	handleAuthNFail : function (obj) {
		this.handle('authNFail', obj);
	},

	checkPreauthZ : function (resourceIds) {
		this.handle('checkPreauthZ', resourceIds);
	},

	checkAuthN : function () {
		this.handle('checkAuthN');
	},

	getAuthN : function (redirectUrl) {
		this.handle('getAuthN', redirectUrl);
	},

	logout : function () {
		this.handle('logout');
	},

	ignoredErrorCodes : [  // errors that aren't failures
		'N001',  // authenticated passively
		// 'N005',  // authentication cancelled
		'N010',  // authenticated while in degradation
		'N011',  // authenticated with TempPass
		'N120',  // multiple authN calls (not exactly an authNFail)
		'CFG100'  // correct clock
	],

	handleAuthError : function (obj) {
		var errData = obj.data;
		var data = {};

		data.errorCode = errData.errorCode || errData.errorId;

		if (errData.message) {
			data.errorString = errData.message;
		}

		if (errData.messageHtml) {
			data.errorHtml = errData.messageHtml;
		}

		if (errData.errorCategory) {
			data.errorCategory = errData.errorCategory;
		}

		if (errData.resolution) {
			data.resolution = errData.resolution;
		}

		if (this.state === 'Initializing') {
			this.handle('error', data);
		}
		else {
			var errorId = errData.errorId;

			if (obj.source === 'adobe' && errorId.charAt(0) === 'Z') {
				_logger.debug('Ignoring authZ error ' + errorId);
				return;
			}
			else if (errorId.indexOf('DRM') === 0 && this.state === 'Authenticated') {
				_logger.debug('Ignoring DRM error ' + errorId);
				return;
			}
			else {
				if (errorId === 'N100' || errorId === 'N110') {
					_logger.debug('Error ' + errorId + ': Expired authN; logging out user.');
					this.requestLogOut();
					return;
				}
				if (_.contains(this.ignoredErrorCodes, errorId)) {
					_logger.debug('Ignoring authN error ' + errorId);
				}
				else {
					this.authManager.getProviderID(function getProviderIDResponse(providerId) {

						data.mvpdId = providerId;

						this.handle('error', data);

					}, this);
				}
			}
		}
	}

});


module.exports = ClientAuthNFsm;
